// src/components/JobListings.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import Papa from "papaparse";
import { motion } from "framer-motion";
import { Briefcase } from "lucide-react";

const JobListings = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const whatsappNumber = "9828113577";

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(
          "https://docs.google.com/spreadsheets/d/e/2PACX-1vRUP8NJVNlS2Nq8RGSzl4quPyUmhKdCrfc1atgC_hWtFhBa_Fc7jfQ9jycYx1V91v1KJG2BGKGgZzz8/pub?output=csv"
        );

        const parsedData = Papa.parse(response.data, {
          header: true,
          skipEmptyLines: true,
        });

        setJobs(parsedData.data.filter((job) => job.Title));
      } catch (error) {
        setError("⚠️ Unable to load job listings. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  return (
    <section
      id="jobs"
      className="py-16 px-6 bg-gradient-to-br from-blue-50 via-white to-green-100"
    >
      <div className="max-w-6xl mx-auto">
        {/* 🔥 Job Banner */}
        {jobs.length > 0 && (
          <div className="overflow-hidden bg-gradient-to-r from-indigo-600 to-green-500 text-white py-3 mb-10 rounded-lg shadow-md">
            <motion.div
              className="flex gap-16"
              animate={{ x: ["100%", "-100%"] }}
              transition={{ repeat: Infinity, duration: 20, ease: "linear" }}
            >
              {jobs.map((job, index) => (
                <span key={index} className="font-medium whitespace-nowrap">
                  🚀 {job.Title} – {job.Location} | {job.Experience} Exp
                </span>
              ))}
            </motion.div>
          </div>
        )}

        {/* Heading */}
        <h2 className="text-4xl font-extrabold text-center mb-12 text-blue-900">
          🚀 Current Job Openings
        </h2>

        {/* Loading/Error States */}
        {loading && (
          <p className="text-center text-gray-600">Loading job listings...</p>
        )}
        {error && <p className="text-center text-red-600">{error}</p>}
        {!loading && jobs.length === 0 && (
          <p className="text-center text-gray-600">
            No job openings available right now. 🚧
          </p>
        )}

        {/* Job Cards Grid */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 items-stretch">
          {jobs.map((job, index) => {
            const message = `Hello, I am interested in applying for the role of ${job.Title}.
Experience: ${job.Experience}
Location: ${job.Location}
Qualification: ${job.Qualification}

Please share more details.`;

            const encodedMessage = encodeURIComponent(message);
            const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

            return (
              <article
                key={index}
                className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between break-words"
              >
                {/* Job Title */}
                <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Briefcase className="text-blue-600 shrink-0" size={22} />
                  <span className="break-words">{job.Title}</span>
                </h3>

                {/* Details Section */}
                <div className="space-y-3 flex-grow">
                  {job.Experience && (
                    <p className="flex items-center gap-2 text-gray-600 break-words">
                      {/* <Clock size={18} className="text-indigo-500 shrink-0" /> */}
                      <span>
                        <strong>Experience:</strong> {job.Experience}
                      </span>
                    </p>
                  )}
                  {job.Location && (
                    <p className="flex items-center gap-2 text-gray-600 break-words">
                      {/* <MapPin size={18} className="text-green-600 shrink-0" /> */}
                      <span>
                        <strong>Location:</strong> {job.Location}
                      </span>
                    </p>
                  )}
                  {job.Qualification && (
                    <p className="flex items-center gap-2 text-gray-600 break-words">
                      {/* <GraduationCap size={18} className="text-purple-600 shrink-0" /> */}
                      <span>
                        <strong>Qualification:</strong> {job.Qualification}
                      </span>
                    </p>
                  )}
                </div>

                {/* Buttons */}
                <div className="mt-6 flex flex-col gap-2">
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-500 text-white font-medium px-4 py-2 rounded-lg hover:bg-green-600 text-center transition"
                  >
                    Apply via WhatsApp
                  </a>
                  {job.ApplyLink && (
                    <a
                      href={job.ApplyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline text-sm text-center hover:text-blue-800"
                    >
                      or Apply Online
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default JobListings;
